import React, { useState } from 'react';
import { Upload, FileSpreadsheet, Check, X, AlertCircle, Download, Eye, Search } from 'lucide-react';
import * as XLSX from 'xlsx';
import ApiService from '../services/ApiService';

const HistoricalWorkOrderImport = ({ onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [xeroMatchResults, setXeroMatchResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Xero Check, 4: Import, 5: Results
  const [importMode, setImportMode] = useState('full'); // 'full' or 'invoice-only'

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    console.log('📁 File selected:', uploadedFile.name);
    setFile(uploadedFile);
    setIsProcessing(true);

    try {
      console.log('📖 Reading Excel file...');
      const data = await readExcelFile(uploadedFile);
      console.log('📊 Raw Excel data:', data);
      
      console.log('🔄 Parsing work order data...');
      const workOrders = parseWorkOrderData(data);
      console.log('✅ Parsed work orders:', workOrders);
      
      setParsedData(workOrders);
      setStep(2);
    } catch (error) {
      console.error('❌ Error parsing file:', error);
      alert('Error parsing file: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  };

  const parseWorkOrderData = (data) => {
    if (data.length < 2) {
      throw new Error('File must contain at least a header row and one data row');
    }

    const headers = data[0];
    const workOrders = [];

    console.log('📋 Headers found:', headers);
    
    // Check import mode based on column count and headers
    if (headers.length === 1 && headers[0].toString().toLowerCase().includes('invoice')) {
      console.log('🔍 Detected invoice-only mode');
      setImportMode('invoice-only');
      
      // Parse as simple invoice number list
      for (let i = 1; i < data.length; i++) {
        const row = data[i];
        if (!row || row.length === 0) continue;
        
        const invoiceNumber = row[0]?.toString().trim();
        if (invoiceNumber) {
          workOrders.push({
            workOrderNumber: invoiceNumber,
            clientName: 'To be loaded from Xero',
            subcontractorName: 'To be determined',
            invoiceAmount: 0,
            cost: 0,
            location: 'To be loaded from Xero',
            description: 'To be loaded from Xero',
            status: 'To be determined',
            department: 'To be determined',
            serviceType: 'To be determined',
            profit: 0,
            needsXeroData: true
          });
        }
      }
      
      console.log('📋 Parsed', workOrders.length, 'invoice numbers for Xero lookup');
      return workOrders;
    }
    
    // Check if this is hybrid mode (WO number + contractor + cost)
    if (headers.length === 3 && 
        headers[0].toString().toLowerCase().includes('work order') &&
        headers[1].toString().toLowerCase().includes('contractor') &&
        headers[2].toString().toLowerCase().includes('cost')) {
      console.log('🔍 Detected hybrid mode: WO + Contractor info');
      setImportMode('hybrid');
      
      // Parse as WO number + contractor details
      for (let i = 1; i < data.length; i++) {
        const row = data[i];
        if (!row || row.length === 0) continue;
        
        const workOrderNumber = row[0]?.toString().trim();
        const subcontractorName = row[1]?.toString().trim();
        const cost = parseFloat(row[2]?.toString().trim() || '0');
        
        if (workOrderNumber && subcontractorName) {
          workOrders.push({
            workOrderNumber,
            subcontractorName,
            cost,
            clientName: 'To be loaded from Xero',
            invoiceAmount: 0, // Will be loaded from Xero
            location: 'To be loaded from Xero',
            description: 'To be loaded from Xero',
            status: 'To be determined',
            department: 'To be determined',
            serviceType: 'To be determined',
            profit: 0,
            needsXeroData: true,
            hasContractorInfo: true
          });
        }
      }
      
      console.log('📋 Parsed', workOrders.length, 'WO + contractor entries for Xero lookup');
      return workOrders;
    }

    // Column mapping for work order data
    const columnMapping = {
      'workOrderNumber': ['work order', 'workorder', 'work order number', 'wo number', 'number'],
      'clientName': ['client', 'client name', 'customer', 'customer name'],
      'subcontractorName': ['subcontractor', 'contractor', 'team', 'assigned to'],
      'invoiceAmount': ['invoice amount', 'amount', 'total', 'invoice total'],
      'cost': ['cost', 'expense', 'contractor cost'],
      'location': ['location', 'address', 'site'],
      'description': ['description', 'work description', 'details'],
      'status': ['status'],
      'department': ['department', 'dept'],
      'serviceType': ['service', 'service type', 'type']
    };

    // Find column indices
    const columnIndices = {};
    Object.keys(columnMapping).forEach(field => {
      const index = headers.findIndex(header => {
        const headerLower = header.toString().toLowerCase().trim();
        return columnMapping[field].some(pattern => 
          headerLower.includes(pattern) || pattern.includes(headerLower)
        );
      });
      if (index !== -1) {
        columnIndices[field] = index;
      }
    });

    console.log('📍 Column mapping found:', columnIndices);

    // Parse data rows
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0) continue;

      // Skip empty rows
      const hasData = row.some(cell => cell !== undefined && cell !== null && cell.toString().trim() !== '');
      if (!hasData) continue;

      try {
        const workOrder = {
          workOrderNumber: getColumnValue(row, columnIndices.workOrderNumber, ''),
          clientName: getColumnValue(row, columnIndices.clientName, ''),
          subcontractorName: getColumnValue(row, columnIndices.subcontractorName, ''),
          invoiceAmount: parseFloat(getColumnValue(row, columnIndices.invoiceAmount, '0')) || 0,
          cost: parseFloat(getColumnValue(row, columnIndices.cost, '0')) || 0,
          location: getColumnValue(row, columnIndices.location, ''),
          description: getColumnValue(row, columnIndices.description, ''),
          status: getColumnValue(row, columnIndices.status, 'Completed'),
          department: getColumnValue(row, columnIndices.department, ''),
          serviceType: getColumnValue(row, columnIndices.serviceType, ''),
          profit: 0 // Will be calculated
        };

        // Calculate profit
        workOrder.profit = workOrder.invoiceAmount - workOrder.cost;

        // Only include if we have essential data
        if (workOrder.workOrderNumber && workOrder.clientName) {
          workOrders.push(workOrder);
        }
      } catch (error) {
        console.warn(`Error parsing row ${i}:`, error);
      }
    }

    if (workOrders.length === 0) {
      throw new Error('No valid work orders found in the file. Please check the format and required columns.');
    }

    return workOrders;
  };

  const getColumnValue = (row, columnIndex, defaultValue = '') => {
    if (columnIndex === undefined || columnIndex === -1 || !row[columnIndex]) {
      return defaultValue;
    }
    return row[columnIndex].toString().trim();
  };

  const downloadTemplate = () => {
    const template = [
      // Header row
      [
        'Work Order Number', 
        'Client Name', 
        'Subcontractor', 
        'Invoice Amount', 
        'Cost', 
        'Location', 
        'Description', 
        'Status', 
        'Priority', 
        'Due Date',
        'Department',
        'Service Type',
        'Date Created'
      ],
      // Sample data rows based on your actual work orders
      [
        'T0035249168',
        'Guardian Asset Management', 
        'garrett',
        '750.00',
        '200.00',
        '4828 Far Hills Dr, Independence, KY',
        'Property maintenance work',
        'Completed',
        'Normal',
        '2025-02-15',
        'GAM',
        'Maintenance',
        '2025-01-15'
      ],
      [
        'T0035016531',
        'ABC Company',
        'rich',
        '375.00',
        '50.00',
        '105 N. 2nd Ave, Jonesboro, IN',
        'Inspection and repair',
        'Completed',
        'High',
        '2025-02-10',
        'GAM',
        'Inspection',
        '2025-01-10'
      ],
      [
        'M15237233',
        'MCS Property Services',
        'brad',
        '632.00',
        '450.00',
        '843 Elwood Ave, Madison, IN',
        'Specialized maintenance',
        'Completed',
        'Normal',
        '2025-02-20',
        'MCS',
        'Specialized',
        '2025-01-20'
      ],
      [
        'T0035668787',
        'Property Management LLC',
        'jason',
        '200.00',
        '150.00',
        '2116 N New York Ave, Muncie, IN',
        'Union grass maintenance',
        'Invoiced',
        'Low',
        '2025-01-30',
        'GAM',
        'Landscaping',
        '2025-01-05'
      ],
      [
        'T0035491841',
        'Facilities Corp',
        'garrett',
        '1054.00',
        '590.00',
        '725 Indiana Ave, Anderson, IN',
        'Union shrubs and landscaping',
        'Invoiced',
        'Normal',
        '2025-02-25',
        'GAM',
        'Landscaping',
        '2025-01-25'
      ]
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    
    // Set column widths for better readability
    const colWidths = [
      { wch: 15 }, // Work Order Number
      { wch: 25 }, // Client Name
      { wch: 15 }, // Subcontractor
      { wch: 12 }, // Invoice Amount
      { wch: 10 }, // Cost
      { wch: 30 }, // Location
      { wch: 25 }, // Description
      { wch: 12 }, // Status
      { wch: 10 }, // Priority
      { wch: 12 }, // Due Date
      { wch: 12 }, // Department
      { wch: 15 }, // Service Type
      { wch: 12 }  // Date Created
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historical Work Orders');
    XLSX.writeFile(wb, 'historical-work-orders-template.xlsx');
  };

  const downloadInvoiceOnlyTemplate = () => {
    const template = [
      // Header row
      ['Invoice Number'],
      // Sample invoice numbers
      ['T0035249168'],
      ['T0035016531'], 
      ['M15237233'],
      ['T0035668787'],
      ['T0035491841']
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    ws['!cols'] = [{ wch: 15 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice Numbers Only');
    XLSX.writeFile(wb, 'invoice-numbers-only-template.xlsx');
  };

  const downloadHybridTemplate = () => {
    const template = [
      // Header row
      ['Work Order Number', 'Subcontractor', 'Cost'],
      // Sample data - WO number + contractor info, system will get invoice details from Xero
      ['T0035249168', 'garrett', '200.00'],
      ['T0035016531', 'Central Copiers', '50.00'],
      ['T0035016531', 'Capital Cab Co', '20.00'], // Same WO, different contractor
      ['M15237233', 'brad', '450.00'],
      ['T0035668787', 'jason', '150.00'],
      ['T0035491841', 'garrett', '590.00']
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    
    // Set column widths
    const colWidths = [
      { wch: 15 }, // Work Order Number
      { wch: 20 }, // Subcontractor
      { wch: 10 }  // Cost
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'WO + Contractor Info');
    XLSX.writeFile(wb, 'workorder-contractor-template.xlsx');
  };

  const handleCheckXeroExisting = async () => {
    setIsProcessing(true);
    
    try {
      console.log('🔍 Bulk loading ALL invoices from Xero for efficient matching...');
      
      // Extract work order numbers from parsed data
      const workOrderNumbers = parsedData.map(wo => wo.workOrderNumber).filter(Boolean);
      
      console.log('📋 Will match', workOrderNumbers.length, 'work order numbers against ALL Xero invoices');
      
      // Use bulk loading approach for maximum efficiency
      const allXeroInvoices = await ApiService.getAllInvoices();
      
      console.log('✅ Bulk loaded', allXeroInvoices.length, 'invoices from Xero in single API call');
      
      // Create lookup dictionary for fast matching
      const invoiceLookup = {};
      allXeroInvoices.forEach(invoice => {
        if (invoice.invoiceNumber) {
          invoiceLookup[invoice.invoiceNumber.toUpperCase()] = invoice;
        }
      });
      
      // Match locally (no more API calls)
      const foundInvoices = [];
      const notFoundNumbers = [];
      
      workOrderNumbers.forEach(workOrderNumber => {
        const foundInvoice = invoiceLookup[workOrderNumber.toUpperCase()];
        if (foundInvoice) {
          foundInvoices.push(foundInvoice);
        } else {
          notFoundNumbers.push(workOrderNumber);
        }
      });
      
      const xeroResults = {
        found: foundInvoices,
        notFound: notFoundNumbers
      };
      
      console.log('✅ Bulk matching complete:', {
        totalInvoicesLoaded: allXeroInvoices.length,
        foundInvoices: foundInvoices.length,
        notFoundInvoices: notFoundNumbers.length
      });
      
      // Match results with parsed data
      const matchedData = parsedData.map(wo => {
        const foundInvoice = xeroResults.found.find(found => found.invoiceNumber === wo.workOrderNumber);
        const existsInXero = !!foundInvoice;
        
        // If needs Xero data (invoice-only or hybrid mode) and found in Xero, populate data from Xero
        if (wo.needsXeroData && foundInvoice) {
          console.log('🔍 Populating Xero data for', wo.workOrderNumber, '- Found invoice:', foundInvoice);
          
          // Create line items for the contractor data
          const lineItems = wo.hasContractorInfo ? [{
            subcontractorName: wo.subcontractorName,
            subcontractorContactId: '', // Will be looked up later
            cost: wo.cost || 0,
            description: `${wo.subcontractorName} work for ${wo.workOrderNumber}`,
            serviceType: 'Imported from historical data',
            notes: `Imported with cost: $${wo.cost}`
          }] : [];
          
          return {
            ...wo,
            clientName: foundInvoice.contactName || 'Client from Xero Invoice',
            xeroContactId: foundInvoice.contactID || '',
            invoiceAmount: foundInvoice.total || wo.invoiceAmount,
            originalInvoiceAmount: foundInvoice.total || wo.invoiceAmount,
            totalCost: wo.cost || 0,
            location: foundInvoice.reference || `Location from invoice ${wo.workOrderNumber}`,
            description: wo.hasContractorInfo ? 
              `${wo.subcontractorName} - Invoice: ${foundInvoice.total ? '$' + foundInvoice.total : 'Amount from Xero'}` :
              `Invoice ${foundInvoice.invoiceNumber} from Xero`,
            status: 'Finalized', // Invoices found in Xero should be marked as Finalized
            department: wo.workOrderNumber.startsWith('M') ? 'MCS' : 'GAM',
            priority: 'Normal',
            profit: (foundInvoice.total || 0) - (wo.cost || 0),
            lineItems: lineItems,
            existsInXero: true,
            xeroInvoiceId: foundInvoice.invoiceID,
            xeroAmountDue: foundInvoice.amountDue || 0,
            xeroTotal: foundInvoice.total || 0,
            xeroDate: foundInvoice.date,
            xeroDueDate: foundInvoice.dueDate,
            xeroStatus: 'Found in Xero - Data Populated',
            recommendedStatus: foundInvoice.amountDue > 0 ? 'Invoiced' : 'Paid',
            needsXeroData: false
          };
        }
        
        return {
          ...wo,
          existsInXero,
          xeroInvoiceId: foundInvoice?.invoiceID,
          xeroAmountDue: foundInvoice?.amountDue || 0,
          xeroTotal: foundInvoice?.total || 0,
          xeroStatus: existsInXero ? 'Found in Xero' : 'Not in Xero',
          recommendedStatus: existsInXero ? 'Invoiced' : 'Completed'
        };
      });
      
      setXeroMatchResults({
        total: parsedData.length,
        foundInXero: xeroResults.found.length,
        notInXero: xeroResults.notFound.length,
        matchedData,
        xeroInvoices: xeroResults.found
      });
      
      setStep(3);
    } catch (error) {
      console.error('Error checking Xero:', error);
      alert('Error checking Xero: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    setIsProcessing(true);
    
    try {
      // Use the matched data if Xero check was performed
      const dataToImport = xeroMatchResults ? xeroMatchResults.matchedData : parsedData;
      
      console.log('📤 Importing', dataToImport.length, 'work orders with Xero status...');
      console.log('🔍 Sample work order data being sent:', dataToImport[0]);
      console.log('🔍 Line items for first work order:', dataToImport[0]?.lineItems);
      
      const result = await ApiService.bulkImportWorkOrders(dataToImport);
      console.log('✅ Import result:', result);
      
      setImportResults(result);
      setStep(4);
      
      if (onImportComplete) {
        onImportComplete(result);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Import failed: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Import Historical Work Orders</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center space-x-2">
              <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>1</div>
                <span className="ml-2 text-sm font-medium">Upload</span>
              </div>
              <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'} rounded`}></div>
              <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>2</div>
                <span className="ml-2 text-sm font-medium">Preview</span>
              </div>
              <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'} rounded`}></div>
              <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>3</div>
                <span className="ml-2 text-sm font-medium">Xero Check</span>
              </div>
              <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'} rounded`}></div>
              <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>4</div>
                <span className="ml-2 text-sm font-medium">Results</span>
              </div>
            </div>
          </div>

          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Import Historical Work Orders</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Upload Excel/CSV files with historical work order data</li>
                  <li>• System will check which ones already exist in Xero</li>
                  <li>• Automatically sets correct status (Invoiced vs Completed)</li>
                  <li>• Prevents duplicate entries and maintains data integrity</li>
                </ul>
              </div>

              <div className="flex justify-center gap-2 mb-4">
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  Full Template
                </button>
                <button
                  onClick={downloadHybridTemplate}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  WO + Contractor
                </button>
                <button
                  onClick={downloadInvoiceOnlyTemplate}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  Invoice Only
                </button>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-yellow-800 mb-2">💡 Three Import Options:</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-green-700">Full Template:</p>
                    <p className="text-green-600">Complete work order data with all fields</p>
                  </div>
                  <div>
                    <p className="font-medium text-purple-700">WO + Contractor: ⭐</p>
                    <p className="text-purple-600">Work order number + contractor details, invoice data from Xero</p>
                  </div>
                  <div>
                    <p className="font-medium text-blue-700">Invoice Only:</p>
                    <p className="text-blue-600">Just invoice numbers, all details from Xero</p>
                  </div>
                </div>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Choose your historical work orders file</p>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Select File
                </label>
              </div>

              {isProcessing && (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-600 mt-2">Processing work order data...</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview Data */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-800 mb-2">Work Order Preview</h3>
                <p className="text-sm text-green-700">
                  Found {parsedData.length} work orders. Next step will check which ones already exist in Xero.
                </p>
              </div>

              <div className="overflow-x-auto max-h-96 border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Work Order #</th>
                      <th className="px-4 py-2 text-left">Client</th>
                      <th className="px-4 py-2 text-left">Team</th>
                      <th className="px-4 py-2 text-right">Invoice Amount</th>
                      <th className="px-4 py-2 text-right">Cost</th>
                      <th className="px-4 py-2 text-right">Profit</th>
                      <th className="px-4 py-2 text-left">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {parsedData.slice(0, 20).map((workOrder, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{workOrder.workOrderNumber}</td>
                        <td className="px-4 py-2">{workOrder.clientName}</td>
                        <td className="px-4 py-2">{workOrder.subcontractorName}</td>
                        <td className="px-4 py-2 text-right">${workOrder.invoiceAmount.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">${workOrder.cost.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">
                          <span className={`font-medium ${workOrder.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ${workOrder.profit.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-2">{workOrder.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {parsedData.length > 20 && (
                <p className="text-sm text-gray-500 text-center">
                  Showing first 20 rows. {parsedData.length - 20} more rows will be imported.
                </p>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleCheckXeroExisting}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Checking Xero...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Check Existing in Xero
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Xero Check Results */}
          {step === 3 && xeroMatchResults && (
            <div className="space-y-6">
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Xero Matching Results</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{xeroMatchResults.total}</p>
                    <p className="text-sm text-gray-600">Total Work Orders</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{xeroMatchResults.foundInXero}</p>
                    <p className="text-sm text-gray-600">Found in Xero</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">{xeroMatchResults.notInXero}</p>
                    <p className="text-sm text-gray-600">Not in Xero</p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto max-h-96 border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Work Order #</th>
                      <th className="px-4 py-2 text-left">Client</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-center">Xero Status</th>
                      <th className="px-4 py-2 text-right">Xero Amount</th>
                      <th className="px-4 py-2 text-left">Recommended Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {xeroMatchResults.matchedData.slice(0, 20).map((workOrder, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium">{workOrder.workOrderNumber}</td>
                        <td className="px-4 py-2">{workOrder.clientName}</td>
                        <td className="px-4 py-2 text-right">${workOrder.invoiceAmount.toFixed(2)}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs ${
                            workOrder.existsInXero ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {workOrder.xeroStatus}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          {workOrder.existsInXero ? `$${workOrder.xeroTotal.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            workOrder.recommendedStatus === 'Invoiced' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {workOrder.recommendedStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Import {xeroMatchResults.matchedData.length} Work Orders
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Results */}
          {step === 4 && importResults && (
            <div className="space-y-6">
              <div className={`rounded-lg p-4 ${importResults.errorCount > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                <h3 className={`text-lg font-semibold mb-2 ${importResults.errorCount > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                  Import Complete
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{importResults.successCount || 0}</p>
                    <p className="text-sm text-gray-600">Successfully Imported</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{xeroMatchResults?.foundInXero || 0}</p>
                    <p className="text-sm text-gray-600">Found in Xero</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">{xeroMatchResults?.notInXero || 0}</p>
                    <p className="text-sm text-gray-600">Not in Xero</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{importResults.errorCount || 0}</p>
                    <p className="text-sm text-gray-600">Errors</p>
                  </div>
                </div>
              </div>

              {importResults.errorCount > 0 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Import Errors
                  </h4>
                  <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {importResults.errors?.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">What Happens Next</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Work orders found in Xero have been marked as "Invoiced"</li>
                  <li>• Work orders not in Xero have been marked as "Completed"</li>
                  <li>• You can now use the finalize process for items not yet in Xero</li>
                  <li>• Check the Work Orders tab to see all imported items</li>
                </ul>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoricalWorkOrderImport;
